import { useNavigate } from '@tanstack/react-router';

import { MaisAcoes, type AcaoDoMenu } from '@/componentes/MaisAcoes';
import { useSessaoEmMemoria } from '@/modulos/sessao/memoria';

import type { AcaoNoAbastecimento } from './EstornoDoAbastecimento';
import { menuDaLinhaDoAbastecimento } from './estorno';
import type { Abastecimento } from './tipos';

/**
 * OS TRÊS PONTOS DA LINHA DO ABASTECIMENTO — o mesmo menu nas duas listas da
 * mesa (decisão 40), e as mesmas janelas (`JanelasDoAbastecimento`).
 *
 * Pedido do dono (27/10/2026): a lista principal do Combustível ganha o MESMO
 * menu da tela de lançamento — corrigir, estornar e, só para o administrador,
 * apagar. "O mesmo" aqui quer dizer o mesmo código: as duas páginas desenham
 * `MenuDaLinhaDoAbastecimento` na linha e `JanelasDoAbastecimento` uma vez, e
 * nenhuma monta menu ou janela por conta própria (o teste
 * `menu-da-linha.test.ts` reprova a que montar). Duas cópias divergem na
 * primeira mudança de licença, e a lista que ficou para trás passa a oferecer
 * o que o servidor recusa — ou a esconder o que ele aceita.
 *
 * A página guarda só QUAL ação está aberta, e em qual linha — o estado que o
 * painel da tela de lançamento também abre ("Corrigir lançamento"). É um
 * componente, e não um gancho, e o estado mora na página, para a análise do
 * H54 (`scripts/lib/acoes-e-licencas.mjs`) seguir a licença do menu até a
 * janela: ela segue componente, estado e quem o muda, e não entra em gancho.
 *
 * O que cada item oferece é `menuDaLinhaDoAbastecimento` (estorno.ts): a
 * licença de estorno liga corrigir e estornar; sem ela, os dois vêm
 * desligados com o motivo; apagar só aparece com `abastecimento.excluir`. A
 * tela só OFERECE — quem garante é a rota (`exige` em backend/src/rotas/
 * combustivel.ts).
 */
export interface AcaoNaLinhaDoAbastecimento {
  readonly acao: AcaoNoAbastecimento;
  readonly alvo: Abastecimento;
}

export function MenuDaLinhaDoAbastecimento({
  abastecimento,
  estornado,
  aoEscolher,
}: {
  readonly abastecimento: Abastecimento;
  readonly estornado: boolean;
  readonly aoEscolher: (escolha: AcaoNaLinhaDoAbastecimento) => void;
}) {
  const permissoes = useSessaoEmMemoria()?.permissoes ?? null;
  const navegar = useNavigate();
  const itens = menuDaLinhaDoAbastecimento(permissoes, estornado);
  if (itens === null) return null;
  /* Os três pontos ficam sempre (p.27): sem a licença, as ações vêm
     desligadas com o motivo, e quem gere a governança recebe o caminho
     para conceder (`menuDoAbastecimento`, ADR-008). */
  const acoes: AcaoDoMenu[] = itens.map((item) => ({
    rotulo: item.rotulo,
    ...(item.motivoIndisponivel ? { motivoIndisponivel: item.motivoIndisponivel } : {}),
    aoEscolher: () => {
      if (item.acao === 'governanca') void navegar({ to: '/cadastros/governanca', search: { vista: 'papeis' } });
      else aoEscolher({ acao: item.acao, alvo: abastecimento });
    },
  }));
  return <MaisAcoes forma="icone" acoes={acoes} />;
}
